<template>
  <v-container class="ma-0 pa-0 mw-100">
    <validation-dialog
      :title="$t('AdminPage.DeleteAddress')"
      :content="$t('AdminPage.DeleteAddressContent')"
      :dialog="deleteAddressDialog"
      @close="(deleteAddressDialog = false), (deleteAddress = '');"
      @done="deleteAddressMethod()"
      :buttons="[$t('Headers.cancel'), $t('Headers.delete')]"
    />
    <v-form ref="form" @keyup.native.enter="submit">
      <validation-observer
        ref="observer"
        v-slot="{ invalid }"
        v-if="!addNewAddressDialog && !changeAddressDialog"
      >
        <!-- HEADER ROW-->
        <v-row justify="center">
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.CompanyName')"
              rules="required|text"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('AdminPage.CompanyName')"
                prepend-inner-icon="mdi-pencil"
                v-model="company.organization_name"
                required
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.SalesTaxNumber')"
              rules="required|text"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('AdminPage.SalesTaxNumber')"
                prepend-inner-icon="mdi-pencil"
                v-model="company.sales_tax_number"
                required
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.ContactPerson')"
              rules="required|name_validate"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('AdminPage.ContactPerson')"
                prepend-inner-icon="mdi-pencil"
                v-model="company.contact_person"
                required
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>
        <v-row class="mx-n5">
          <v-col class="text-right">
            <custom-button
              :disabled="invalid"
              @click="submit"
              :text="$t('Headers.save')"
            ></custom-button>
          </v-col>
        </v-row>
      </validation-observer>
    </v-form>
    <!--ADRESS HEADER-->
    <header-small :text="$t('AdminPage.Adress')" />
    <!-- ONE ADRESS-->
    <v-container class="ma-0 pa-0 mw-100" v-if="singleAddress && !addNewAddressDialog && !changeAddressDialog">
      <orga-dialog :addresses="company.addresses" :newAddress="false" />
    </v-container>
    <!--ADD NEW ADDRESS-->
    <v-container class="ma-0 pa-0 mw-100" v-if="addNewAddressDialog">
      <orga-dialog
        :addresses="newAddress"
        :newAddress="true"
        @close="
          (addNewAddressDialog = false), (newAddress = {}), getData, reload;
        "
      />
    </v-container>
    <!--CHANGE ADDRESS-->
    <v-container class="ma-0 pa-0 mw-100" v-if="changeAddressDialog">
      <orga-dialog
        :addresses="changeAddress"
        :newAddress="false"
        @close="
          (changeAddressDialog = false), (changeAddress = {}), getData, reload;
        "
      />
    </v-container>
    <!--MULTIPLE ADDRESSES-->
    <v-container
      class="pt-5 pa-0"
      v-if="multipleAddresses && !addNewAddressDialog && !changeAddressDialog"
    >
      <custom-table
        :search="search"
        :items="company.addresses"
        :headers="addressHeaders"
        @edit="(changeAddressDialog = true), (changeAddress = $event);"
        @delete="(deleteAddressDialog = true), (deleteAddress = $event);"
      />
    </v-container>

    <v-row
      class="pt-6 mx-n1"
      v-if="!changeAddressDialog && !addNewAddressDialog"
    >
      <v-spacer />
      <custom-button
        @click="addNewAddressDialog = true"
        :text="$t('AdminPage.Button_4')"
        :color="color"
        icon="mdi-plus-circle-outline"
        class="mx-3"
      >
      </custom-button>
    </v-row>
  </v-container>
</template>

<script>
import i18n from "@/i18n";
import CustomButton from "../../components/ui/input/CustomButton";
import CustomTable from "../../components/ui/other/CustomTable";
import HeaderSmall from "../../components/ui/text/HeaderSmall";
import ValidationDialog from "../../components/ui/other/ValidationDialog";
import OrgaDialog from "./OrgaDialog.vue";
import {
  extend,
  ValidationProvider,
  setInteractionMode,
  ValidationObserver,
} from "vee-validate";
import { required, email } from "vee-validate/dist/rules";
import { log } from "@/helpers/logger";

setInteractionMode("eager");

/**
 * @vue-prop {String} title - dialog title
 * @vue-prop {Object} data - stores the dialog data
 * @vue-data {Dictionary} user - stores the user
 * @vue-data {Dictionary} user_reset - stores the start data
 * @vue-data {Array} licenses - stores the selectable licenses
 * @vue-data {Array} roles - stores the selectable roles
 * @vue-data {Boolean}[loading=false] loading - gives, if the content is loading
 * @vue-data {Boolean} rules - checks, if field is required
 * @vue-event {} submit() - saves the user data to DB
 * @vue-event {} setUser() - saves data in the user variable
 * @vue-event {} getData() - gets license and role data from DB
 * @vue-event {} close() - sends close signal to vuex store
 */

extend("required", {
  ...required,
  message: i18n.t("FormValidation.required"),
});

extend("email", {
  ...email,
  message: i18n.t("FormValidation.email"),
});

extend("name_validate", {
  validate: (value) =>
    /(^[a-zA-ZäÄöÖüÜßàÀèÈìÌòÒùÙçÇãÃñÑõÕëËïÏâÂêÊîÎôÔûÛáÁéÉíÍóÓúÚýÝ\-.\s]+)$/.test(
      value
    ),
  message: i18n.t("FormValidation.name"),
});

extend("text", {
  validate: (value) =>
    /(^[a-zA-Z0-9äÄöÖüÜßàÀèÈìÌòÒùÙçÇãÃñÑõÕëËïÏâÂêÊîÎôÔûÛáÁéÉíÍóÓúÚýÝ\-.&\s]+)$/.test(
      value
    ),
  message: i18n.t("FormValidation.field"),
});

export default {
  name: "organization",
  components: {
    CustomButton,
    CustomTable,
    HeaderSmall,
    ValidationProvider,
    ValidationObserver,
    OrgaDialog,
    ValidationDialog,
  },
  title: i18n.t("HomePage.header.admin"),
  data() {
    return {
      company: {},
      newAddress: {},
      changeAddress: {},
      deleteAddress: {},
      licenses: [],
      roles: [],
      singleAddress: false,
      multipleAddresses: false,
      addNewAddressDialog: false,
      changeAddressDialog: false,
      deleteAddressDialog: false,
      selectedAddress: "",
      color: "",
      search: "",
      rules: {
        required: (value) => !!value || this.$t("Rules.required"),
      },
      addressHeaders: [
        {
          text: this.$t("AdminPage.StreetName"),
          align: "start",
          value: "street_name",
        },
        { text: this.$t("AdminPage.StreetNumber"), value: "street_number" },
        { text: this.$t("AdminPage.AdditionalLine"), value: "additional_line" },
        {
          text: this.$t("AdminPage.ZipCode"),
          value: "zip_code",
        },
        {
          text: this.$t("AdminPage.CityName"),
          value: "city",
        },
        {
          text: this.$t("AdminPage.Country"),
          value: "country",
        },
        {
          text: this.$t("AdminPage.AddressTyp"),
          value: "address_type_text",
        },
        {
          text: this.$t("AdminPage.ContactPerson"),
          value: "contact_person",
        },
        { value: "actions", sortable: false },
        {
          value: "delete",
          text: "",
          sortable: false,
        },
      ],
    };
  },
  created() {
    this.getData();
    this.color = this.$vuetify.theme.themes.light.primary;
    this.$store.commit("set_loading", false);
  },
  methods: {
    submit() {
      this.$store.commit("set_loading", true);
      if (this.company) {
        if (!this.$refs.form.validate()) {
          this.$emit("close");
          this.$store.commit("set_loading", false);
          return false;
        }
        this.company.organization_id = this.data.organization_id;
        this.company.organization_active = this.data.organization_active;
        var organization = { organization: this.company };
        this.$store
          .dispatch("data/updateOrganization", organization)
          .then(() => {
            log(
              "AdminPage.Organization.submit() - update organization " +
                organization.organization_id,
              "info"
            );
            this.$emit("close", true);
            location.reload();
          })
          .catch((err) => {
            log("AdminPage.Organization.submit() - " + err, "error");
            this.$emit("close");
          });
      }
    },
    setCompany() {
      this.company = {
        organization_name: this.data.organization_name,
        sales_tax_number: this.data.sales_tax_number,
        contact_person: this.data.contact_person,
        addresses: this.data.addresses,
        //addresses: this.data.addresses[0],
      };
      if (Array.isArray(this.company.addresses)) {
        this.multipleAddresses = true;

        let addresses_temp = this.company.addresses;
        addresses_temp.forEach((us) => {
          us.address_type_text = this.$t("TableContent." + us.address_type);

          us.dropdown_items = [];

          us.dropdown_items.push({
            textButton: false,
            title: "Bearbeiten",
            function: "edit",
            icon: "mdi-pencil",
            key: "edit",
            column: "actions",
          });
        });
        this.company.addresses = addresses_temp;
      } else {
        this.singleAddress = true;
      }
    },
    getData() {
      this.$store
        .dispatch(
          "data/getOrganization",
          this.$store.state.auth.user.organization_id +
            "/" +
            this.$store.state.auth.user.id
        )
        .then((res) => {
          log(
            "AdminPage.Organization.getData() - get organization " +
              this.$store.state.auth.user.organization_id,
            "info"
          );
          this.data = res;
          this.setCompany();
        })
        .catch((err) => {
          log("AdminPage.Organization.getData() - " + err, "error");
        });
    },
    deleteAddressMethod() {
      if (this.deleteAddress.address_type !== "billing address") {
        this.$store
          .dispatch("data/deleteAddress", this.deleteAddress.address_id)
          .then((res) => {
            log(
              "AdminPage.Organization.deleteAddressMethod() - delete address: " +
                this.deleteAddress.address_id,
              "info"
            );
            this.data = res;
            this.deleteAddressDialog = false;
            this.getData();
            location.reload();
          })
          .catch((err) => {
            log("AdminPage.Organization.deleteAddressMethod() - " + err, "error");
          });
      } else {
        this.$store.commit("ui/set_notification", {
          display: true,
          code: this.$t("AdminPage.BillingAddressDeleteError"),
          alertClass: "red",
        });
      }
    },
    close() {
      this.$emit("close");
    },
    reload() {
      location.reload();
    },
  },
};
</script>

<style scoped>
@media screen {
  .container {
    max-width: 100%;
  }
}
</style>
